import { supabase } from '../../shared/supabase/supabaseClient';
import type { PlanEntitlementsRow } from './domain/planEntitlements';
import { debugServiceFetchWarn } from '../../shared/dev/debugServiceFetches';
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit';
import { ENTITLEMENTS_CACHE_MS } from '../session/sessionCacheTtl';

export { ENTITLEMENTS_CACHE_MS };

let entitlementsCache: { value: PlanEntitlementsRow | null; fetchedAt: number } | null = null;
let entitlementsInFlight: Promise<PlanEntitlementsRow | null> | null = null;

export function primeOrganizationEntitlementsCache(row: PlanEntitlementsRow | null): void {
  entitlementsCache = { value: row, fetchedAt: Date.now() };
}

export function getOrganizationEntitlementsCacheIfFresh(
  now = Date.now()
): PlanEntitlementsRow | null | undefined {
  if (!entitlementsCache || now - entitlementsCache.fetchedAt >= ENTITLEMENTS_CACHE_MS) {
    return undefined;
  }
  return entitlementsCache.value;
}

export function isOrganizationEntitlementsCacheFresh(now = Date.now()): boolean {
  return entitlementsCache != null && now - entitlementsCache.fetchedAt < ENTITLEMENTS_CACHE_MS;
}

export function setOrganizationEntitlementsCache(row: PlanEntitlementsRow | null): void {
  entitlementsCache = { value: row, fetchedAt: Date.now() };
}

export function clearOrganizationEntitlementsCache(): void {
  entitlementsCache = null;
}

/** Plan permite módulo Customer (manual y/o link de pago). Sin filas de plan, no bloqueamos (fail-open). */
export function planAllowsCustomer(entitlements: PlanEntitlementsRow | null | undefined): boolean {
  if (entitlements == null) return true;
  return entitlements.fiado_manual_only === true || entitlements.fiado_payment_link === true;
}

/** Plan permite módulo Promociones POS. Sin filas de plan, no bloqueamos (fail-open). */
export function planAllowsPromotions(entitlements: PlanEntitlementsRow | null | undefined): boolean {
  if (entitlements == null) return true;
  return entitlements.promotions_enabled === true;
}

/** Normaliza la respuesta PostgREST del RPC (objeto, JSON string o fila única en array). */
export function normalizeEntitlementsRpcPayload(data: unknown): PlanEntitlementsRow | null {
  let v: unknown = data;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v) as unknown;
    } catch {
      return null;
    }
  }
  if (v == null) return null;
  if (Array.isArray(v)) {
    if (v.length === 1 && v[0] != null && typeof v[0] === 'object' && !Array.isArray(v[0])) {
      v = v[0];
    } else {
      return null;
    }
  }
  if (typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (typeof o.plan_code !== 'string' || typeof o.organization_id !== 'string') {
    return null;
  }
  return v as PlanEntitlementsRow;
}

/**
 * Entitlements del plan (`billing.plan_entitlements` vía RPC). MP QR POS requiere `mp_qr_pos === true`
 * (disponible en Gratis y Crece).
 */
export async function fetchMyOrganizationEntitlements(options?: {
  force?: boolean;
}): Promise<PlanEntitlementsRow | null> {
  const stack = new Error().stack?.split('\n').slice(2, 5).join(' | ') ?? 'unknown';
  debugServiceFetchWarn('[planEntitlements] fetchMyOrganizationEntitlements llamado desde:', stack);
  const now = Date.now();
  if (!options?.force) {
    const cached = getOrganizationEntitlementsCacheIfFresh(now);
    if (cached !== undefined) {
      debugServiceFetchWarn('[planEntitlements] retornando cache');
      return cached;
    }
  }
  if (entitlementsInFlight) {
    debugServiceFetchWarn('[planEntitlements] coalesciendo con in-flight');
    return entitlementsInFlight;
  }

  entitlementsInFlight = fetchMyOrganizationEntitlementsRemote().finally(() => {
    entitlementsInFlight = null;
  });
  return entitlementsInFlight;
}

async function fetchMyOrganizationEntitlementsRemote(): Promise<PlanEntitlementsRow | null> {
  recordStartupNetworkCall('entitlements', 'get_my_organization_entitlements');
  const { data, error } = await supabase.rpc('get_my_organization_entitlements');
  if (error) {
    console.warn('[planEntitlements]', error.message, error.code ?? '');
    setOrganizationEntitlementsCache(null);
    return null;
  }
  const row = normalizeEntitlementsRpcPayload(data);
  if (row == null && data != null) {
    console.warn('[planEntitlements] respuesta RPC con formato inesperado', typeof data);
  }
  setOrganizationEntitlementsCache(row);
  return row;
}
